import {
	ErrorModal,
	getMomentTz,
	TimeService,
	AssetTreePicker,
} from "@dexteel/mesf-core";
import {
	CircularProgress,
	createStyles,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	type DialogProps,
	DialogTitle,
	makeStyles,
	TextField,
	type Theme,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { CraftPicker } from "../../../shared/work-orders-modals/components/picker/craftPicker";
import { useTemplatesContext } from "../context/templatesContext";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import { upsertTemplate } from "../repositories/TemplatesRepositorie";
import { ScheduleCodePicker } from "./picker/ScheduleCodePicker";
import cronstrue from "cronstrue";
import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import { DateTimePicker } from "@mui/x-date-pickers";
import { TableTemplateResoures } from "./template-resources/table-template-resoures";

const moment = getMomentTz();

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiFormLabel-root": {
				transform: "translate(14px, -6px) scale(0.75) ",
				backgroundColor: "#FFFF",
				transition: "none !important",
				left: -3.5,
				padding: "0 5px",
			},
			"& .MuiInputAdornment-root": {
				marginTop: "-8px",
			},
			"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: "#3F51B5",
			},
			"& .MuiFormLabel-root.Mui-focused": {
				color: "#3F51B5",
			},
		},
		assetPicker: {
			"& .MuiGrid-item": {
				width: "500px",
			},
		},
		inputEditable: {
			"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
				border: "2px solid #3F51B5",
			},
		},
		title: {
			fontSize: 15,
			margin: "7px 0",
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
		infoLabel: {
			fontSize: 12,
			color: "#757575",
		},
		reponsive: {
			[theme.breakpoints.up("md")]: {
				padding: "0 !important",
			},
		},
		reponsiveTwo: {
			[theme.breakpoints.up("md")]: {
				padding: "0 10px 0 0 !important",
			},
		},
		disabledDatePicker: {
			"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
				{
					borderWidth: "1px !important",
					borderColor: "rgba(0, 0, 0, 0.23)",
				},
			"& .MuiInputBase-input.Mui-disabled": {
				color: "rgba(0, 0, 0, 0.38)",
			},
			"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
				borderWidth: "1px",
			},
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
};

export const CreateTemplate = ({ show, onHide }: Props) => {
	const {
		state: {
			INITIAL_VALUE_TEMPLATE: { AssetId },
		},
		state: { INITIAL_VALUE_TEMPLATE },
		state,
		actions: { setAssetId, setRowSelectedTemplate },
	} = useTemplatesContext();

	const [open, setOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const classes = useStyles();
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [error, setError] = useState<string>("");
	const [editTemplateError, setEditTemplateError] = useState<string>("");
	const [enableScheduleStart, setEnableScheduleStart] = useState<boolean>(true);
	const [newMode, setNewMode] = useState(true);
	const [assetChanged, setAssetChanged] = useState(false);
	const [cronExpression, setCronExpression] = useState("");
	const [showResourcesDialog, setShowResourcesDialog] = useState(false);
	const [openTemplateResources, setOpenTemplateResources] = useState(false);
	const [createdTemplateId, setCreatedTemplateId] = useState<number | null>(
		null,
	);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
		watch,
	} = useForm<ParametersNewTemplate>({ defaultValues: INITIAL_VALUE_TEMPLATE });

	const description = watch("Description");
	const schedulingPolicyCode = watch("SchedulingPolicyCode");

	const changeSearchAsset = (value: number | null) => {
		if (value !== null) {
			setValue("AssetId", value);
			setAssetId(value);
			setAssetChanged(true);
		}
	};

	const handleCancel = () => {
		onHide(false);
		setNewMode(true);
	};

	const onSubmit: SubmitHandler<ParametersNewTemplate> = async (data: any) => {
		setIsSubmitLoading(true);

		const submitData = {
			...data,
			CronExpression: data.SchedulingPolicyCode === "P" ? cronExpression : "",
		};

		const res = await upsertTemplate(submitData);
		if (res.ok) {
			setNewMode(false);
			const templateId = res.data?.tables?.[0]?.rows?.[0]?.TemplateId;
			setCreatedTemplateId(templateId);

			setRowSelectedTemplate({
				...submitData,
				TemplateId: templateId,
			});

			setAssetChanged(false);
			setIsSubmitLoading(false);
			setShowResourcesDialog(true);
		} else {
			setEditTemplateError(res.message);
			setIsSubmitLoading(false);
		}
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const handleOpenTemplateResources = () => {
		setShowResourcesDialog(false);
		setOpenTemplateResources(true);
	};

	const handleCloseTemplateResources = (shouldUpdate: boolean) => {
		setOpenTemplateResources(false);
		onHide(true);
		setOpen(true);
	};

	const handleSkipResources = () => {
		setShowResourcesDialog(false);
		onHide(true);
		setOpen(true);
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("ScheduleStart", date);
	};

	useEffect(() => {
		if (schedulingPolicyCode === "P") {
			setEnableScheduleStart(false);
		} else {
			setEnableScheduleStart(true);
		}
	}, [schedulingPolicyCode]);

	useEffect(() => {
		reset();
		setAssetId(1);
		setValue("AssetId", 1);
		setValue("ScheduleStart", moment().startOf("day").toDate());
		setCronExpression("");
		setIsModalOpen(false);
	}, [show]);

	return (
		<>
			<Grid container>
				<Grid item>
					<Dialog
						fullWidth={fullWidth}
						maxWidth={maxWidth}
						open={show}
						onClose={() => handleCancel()}
					>
						<form
							onSubmit={handleSubmit(onSubmit)}
							style={{ paddingTop: "8px" }}
						>
							<Grid item md={12} xs={12}>
								<DialogTitle
									style={{
										padding: "0px 24px 0px 24px",
										margin: "0px",
										width: "100%",
									}}
								>
									<Grid item md={12} xs={12}>
										<h1
											style={{
												fontSize: "1.7em",
												margin: "0px",
												padding: "8px 0px",
											}}
										>
											NEW TEMPLATE
										</h1>
									</Grid>
								</DialogTitle>
							</Grid>
							<DialogContent
								style={{ padding: "0 24px", overflow: "visible" }}
								dividers
							>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
									style={{ marginBottom: 0 }}
								>
									TEMPLATE TITLE
								</h6>
								<Grid container spacing={1}>
									<Grid item md={12} xs={12}>
										<Controller
											name="Title"
											control={control}
											rules={{
												validate: (value) => value !== "",
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label="Title"
													variant="outlined"
													fullWidth
													error={!!errors.Title}
													placeholder="Title of the new template"
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
										{errors.Title?.type === "required" && (
											<span className={classes.errorLabel}>
												Title is required
											</span>
										)}
									</Grid>
								</Grid>
								<h6
									className={classes.title}
									style={{ margin: "5px 0 -5px" }}
									id="contained-modal-title-vcenter"
								>
									MAIN PROPERTIES
								</h6>
								<Grid container>
									<Grid
										container
										item
										md={12}
										xs={12}
										style={{ marginTop: 10, padding: 0 }}
										className={classes.assetPicker}
									>
										<Grid
											item
											md={6}
											xs={12}
											style={{ padding: "0 10px 0 0" }}
											className={classes.inputEditable}
										>
											<Controller
												name="AssetId"
												control={control}
												render={({ field: { value } }) => (
													<AssetTreePicker
														label="Asset"
														value={value || 0}
														onChange={changeSearchAsset}
													/>
												)}
											/>
										</Grid>
										<Grid item md={4} xs={6} style={{ padding: "0 10px 0 0" }}>
											<Controller
												name="CraftId"
												control={control}
												render={({ field: { onChange, value } }) => (
													<CraftPicker
														value={value as number}
														onChange={onChange}
													/>
												)}
											/>
										</Grid>
										<Grid item md={2} xs={6}>
											<Controller
												name="TypeCode"
												control={control}
												render={({}) => (
													<TextField
														label="Type"
														variant="outlined"
														disabled
														value={state.INITIAL_VALUE_TEMPLATE.TypeCode}
														fullWidth
														margin="dense"
														autoComplete="off"
														InputLabelProps={{
															style: {
																fontSize: "14px",
															},
														}}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
									style={{ marginBottom: 5 }}
								>
									SCHEDULING
								</h6>
								<Grid container item md={12} xs={12}>
									<Grid item md={3} xs={6} style={{ padding: "0 10px 0 0" }}>
										<Controller
											name="SchedulingPolicyCode"
											control={control}
											render={({ field: { onChange, value } }) => (
												<ScheduleCodePicker
													value={value}
													onChange={(newValue: string) => {
														onChange(newValue);
													}}
													cronExpression={cronExpression}
													onCronExpressionChange={setCronExpression}
													isModalOpen={isModalOpen}
													setIsModalOpen={setIsModalOpen}
													hideScheduleStart={setEnableScheduleStart}
													onScheduleStartChange={handleScheduleStartChange}
												/>
											)}
										/>
									</Grid>

									{enableScheduleStart && (
										<Grid item md={2} xs={6} style={{ padding: "0 10px 0 0" }}>
											<Controller
												name="selectedPeriodDays"
												control={control}
												rules={{
													validate: (value) => value !== 0,
												}}
												render={({ field: { onChange, value } }) => (
													<TextField
														className={classes.root}
														type="number"
														label="Repeat every"
														variant="outlined"
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value}
														onChange={(e) => onChange(e)}
														InputLabelProps={{
															style: {
																fontSize: "14px",
															},
														}}
													/>
												)}
											/>
										</Grid>
									)}

									<Grid
										item
										md={enableScheduleStart ? 3 : 5}
										xs={enableScheduleStart ? 6 : 6}
										style={{ padding: "0 20px 0 10px" }}
										className={classes.root}
									>
										<Controller
											name="ScheduleStart"
											control={control}
											render={({ field }) => (
												<>
													<DateTimePicker
														label="Schedule Start"
														timezone={
															enableScheduleStart
																? TimeService.getInstance().getServerTimeZone()
																: "UTC"
														}
														format={
															enableScheduleStart
																? "MM/DD/YYYY HH:mm"
																: "MM/DD/YYYY"
														}
														timeSteps={{ minutes: 1 }}
														value={moment(field.value) as any}
														onChange={field.onChange}
														inputRef={field.ref}
														disabled={schedulingPolicyCode === "P"}
														sx={{
															"& .MuiFormLabel-root": {
																marginTop: "5px !important",
															},
															"& .MuiButtonBase-root": {
																marginTop: "8px !important",
															},
														}}
														slotProps={{
															textField: {
																fullWidth: true,
																variant: "outlined",
																error: !!errors.ScheduleStart,
																className: classes.disabledDatePicker,
																InputProps: {
																	style: {
																		height: "40px",
																		marginTop: 8.5,
																	},
																},
																inputProps: {
																	style: {
																		paddingTop: "16px",
																	},
																},
															},
														}}
													/>
													{errors.ScheduleStart && (
														<span className={classes.errorLabel}>
															{errors.ScheduleStart.message}
														</span>
													)}
												</>
											)}
										/>
									</Grid>

									<Grid item md={2} xs={6} className={classes.reponsiveTwo}>
										<Controller
											name="selectedExpectedDays"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.root}
													type="number"
													label="Expected duration"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
									</Grid>

									<Grid item md={2} xs={6} style={{ padding: "0" }}>
										<Controller
											name="selectedWarningDays"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.root}
													type="number"
													label="Warn after"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid>
									{schedulingPolicyCode === "P" && (
										<Grid item md={12} xs={12}>
											<TextField
												label="Schedule"
												variant="outlined"
												fullWidth
												margin="dense"
												placeholder="Choose your schedule"
												value={
													cronExpression.length > 0
														? cronstrue.toString(cronExpression as string)
														: ""
												}
												InputProps={{
													readOnly: true,
													endAdornment: (
														<IconButton
															edge="end"
															onClick={() => setIsModalOpen(true)}
															size="small"
														>
															<EditIcon />
														</IconButton>
													),
												}}
											/>
										</Grid>
									)}
								</Grid>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
									style={{ marginBottom: 5 }}
								>
									WORK TIME
								</h6>
								<Grid container item md={12} xs={12}>
									<Grid item md={4} xs={4} style={{ padding: "0 10px 0 0" }}>
										<Controller
											name="PlannedHours"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													type="number"
													label="Planned Hours"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid>
									<Grid
										item
										md={4}
										xs={4}
										className={classes.reponsiveTwo}
										style={{ padding: "0 0 0 0 !important" }}
									>
										<Controller
											name="DownHours"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.root}
													type="number"
													label="Down time Hours"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid>

									<Grid
										item
										md={4}
										xs={4}
										className={classes.reponsive}
										style={{ padding: "0 0 0 10px" }}
									>
										<Controller
											name="NumberOfWorkers"
											control={control}
											rules={{
												min: 1,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													type="number"
													label="Workers Needed"
													variant="outlined"
													error={!!errors.NumberOfWorkers}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) =>
														Number.parseInt(e.target.value as any) >= 1 &&
														onChange(e)
													}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid>
								</Grid>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									MISC
								</h6>
								<Grid
									item
									md={12}
									xs={12}
									style={{ margin: "5px 0 0", padding: "0px" }}
								>
									<Controller
										name="Description"
										control={control}
										render={({ field: { value, onChange } }) => (
											<TextField
												className={classes.root}
												style={{ margin: "8px 0" }}
												size="medium"
												maxRows={5}
												label="Description"
												multiline
												variant="outlined"
												fullWidth
												autoComplete="off"
												value={value}
												onChange={(evt) => {
													if (evt.target.value.length <= 400) onChange(evt);
												}}
											/>
										)}
									/>
									<Grid container style={{ position: "relative" }}>
										<Grid item style={{ padding: 0 }}>
											<span
												className={classes.infoLabel}
												style={
													(description?.length as number) >= 1 &&
													(description?.length as number) !== 400
														? {
																marginLeft: 5,
																visibility: "visible",
																position: "absolute",
																left: 0,
															}
														: { visibility: "hidden" }
												}
											>{`${description?.length}/400 max.`}</span>
											<span
												className={classes.infoLabel}
												style={
													(description?.length as number) == 400
														? {
																marginLeft: 5,
																visibility: "visible",
																position: "absolute",
																left: 0,
															}
														: { visibility: "hidden" }
												}
											>
												Max. 400
											</span>
										</Grid>
									</Grid>
								</Grid>
							</DialogContent>
							<DialogActions style={{ padding: "0", margin: "10px 0" }}>
								<Grid
									container
									spacing={2}
									justifyContent="flex-end"
									style={{ margin: "0 15px" }}
								>
									<Grid
										item
										md={4}
										xs={4}
										spacing={2}
										style={{
											display: "flex",
											alignItems: "center",
											marginRight: "auto",
										}}
										title={
											assetChanged
												? "You need to save to be able to manage resources"
												: ""
										}
									></Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={handleCancel}
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={
												isSubmitLoading ||
												(schedulingPolicyCode === "P" &&
													(!cronExpression ||
														cronExpression.trim().length === 0))
											}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid>
								</Grid>
							</DialogActions>
						</form>
					</Dialog>
				</Grid>
			</Grid>

			<Dialog
				open={showResourcesDialog}
				onClose={handleSkipResources}
				aria-labelledby="add-resources-dialog-title"
			>
				<DialogTitle id="add-resources-dialog-title">
					Add Resources to Template
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						The template was created successfully. Would you like to add
						resources to this template now?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						variant="contained"
						color="default"
						onClick={handleSkipResources}
					>
						CLOSE
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenTemplateResources}
						autoFocus
					>
						ADD RESOURCES
					</Button>
				</DialogActions>
			</Dialog>
			{openTemplateResources && (
				<TableTemplateResoures
					show={openTemplateResources}
					onHide={handleCloseTemplateResources}
					AssetId={watch("AssetId")}
					TemplateId={createdTemplateId}
					newMode={true}
					Title={watch("Title") as string}
					AssetName={(watch("AssetName") as string) || ""}
					TypeCode={state.INITIAL_VALUE_TEMPLATE.TypeCode as string}
					CraftId={watch("CraftId") as number}
				/>
			)}

			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="success" onClose={handleClose}>
					The template was created successfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={editTemplateError}
				onHide={() => setEditTemplateError("")}
			/>
		</>
	);
};
